/**
 * Firebase client SDK — used for password reset (sendPasswordResetEmail / confirmPasswordReset),
 * client-side Auth, and direct Firestore writes (e.g. booking requests from BookNow.tsx).
 * Config comes from import.meta.env (VITE_FIREBASE_*).
 */
import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, type Auth } from "firebase/auth";
import {
  getFirestore,
  enableMultiTabIndexedDbPersistence,
  type Firestore,
} from "firebase/firestore";
import { FIRESTORE_ARTIFACTS_APP_ID } from '../shared-config';

export { FIRESTORE_ARTIFACTS_APP_ID };

const env = typeof import.meta !== "undefined" && (import.meta as unknown as { env?: Record<string, string> }).env
  ? (import.meta as unknown as { env: Record<string, string> }).env
  : {} as Record<string, string>;

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY ?? "",
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
  projectId: env.VITE_FIREBASE_PROJECT_ID ?? "",
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET ?? "",
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
  appId: env.VITE_FIREBASE_APP_ID ?? "",
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let firestorePersistenceRequested = false;

/**
 * Returns the Firebase Auth instance, initializing the app from env if needed.
 * Returns null if VITE_FIREBASE_API_KEY or VITE_FIREBASE_PROJECT_ID are not set.
 */
export function getFirebaseAuth(): Auth | null {
  if (auth) return auth;
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) return null;
  if (!getApps().length) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0] as FirebaseApp;
  }
  auth = getAuth(app);
  // Explicitly set local persistence so the session survives across browser tabs
  setPersistence(auth, browserLocalPersistence).catch(() => {});
  return auth;
}

/**
 * Returns the Firebase app instance (for use with getAuth(app) or other services).
 */
export function getFirebaseApp(): FirebaseApp | null {
  if (app) return app;
  getFirebaseAuth();
  return app;
}

/**
 * Returns the Firestore instance for client-side writes (e.g. booking requests).
 */
export function getFirestoreDb(): Firestore | null {
  if (db) return db;
  const firebaseApp = getFirebaseApp();
  if (!firebaseApp) return null;
  db = getFirestore(firebaseApp);
  if (!firestorePersistenceRequested && typeof window !== "undefined") {
    firestorePersistenceRequested = true;
    // Multi-tab IndexedDB sync (replaces single-tab enableIndexedDbPersistence) — avoids
    // failed-precondition lease fights when Lorena runs several Portal tabs.
    enableMultiTabIndexedDbPersistence(db).catch((err: { code?: string; message?: string }) => {
      if (err?.code === "failed-precondition") {
        console.warn("[Firestore] Multi-tab persistence could not start (see Firebase docs for failed-precondition).");
      } else if (err?.code === "unimplemented") {
        console.warn("[Firestore] IndexedDB persistence not supported in this environment.");
      } else {
        console.warn("[Firestore] enableMultiTabIndexedDbPersistence:", err?.message || err);
      }
    });
  }
  return db;
}

/** @deprecated Prefer FIRESTORE_ARTIFACTS_APP_ID in new code; kept for compatibility. */
export function getFirebaseAppId(): string {
  return FIRESTORE_ARTIFACTS_APP_ID;
}